(function () {
  'use strict';

  const money = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  });

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const cleanImageUrl = (url, width) => {
    if (!url) return '';
    const absolute = url.startsWith('//') ? `https:${url}` : url;
    try {
      const imageUrl = new URL(absolute, window.location.origin);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.toString();
    } catch (error) {
      return absolute;
    }
  };

  class ArgentumProductCard extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      this.setAttribute('aria-busy', 'true');
      this.renderLoading();
      this.loadProduct();
    }

    get handle() {
      return (this.getAttribute('product-handle') || '').trim();
    }

    get productUrl() {
      return `/products/${encodeURIComponent(this.handle)}`;
    }

    async loadProduct() {
      if (!this.handle) {
        this.renderError();
        return;
      }

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}products/${encodeURIComponent(this.handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const product = await response.json();
        this.renderProduct(product);
      } catch (error) {
        this.renderError();
      }
    }

    renderLoading() {
      this.innerHTML = `
        <div class="apc-card apc-card--loading" aria-hidden="true">
          <div class="apc-card__media apc-skeleton"></div>
          <div class="apc-card__content">
            <span class="apc-skeleton apc-skeleton--line"></span>
            <span class="apc-skeleton apc-skeleton--title"></span>
            <span class="apc-skeleton apc-skeleton--button"></span>
          </div>
        </div>`;
    }

    renderProduct(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const available = variants.filter((variant) => variant.available);
      const pricedVariants = available.length ? available : variants;
      const prices = pricedVariants.map((variant) => Number(variant.price)).filter(Number.isFinite);
      const minPrice = prices.length ? Math.min(...prices) : Number(product.price || 0);
      const maxPrice = prices.length ? Math.max(...prices) : minPrice;
      const pricePrefix = maxPrice > minPrice ? 'From ' : '';
      const image = product.featured_image || product.images?.[0] || '';
      const eyebrow = this.getAttribute('eyebrow') || 'Born in Bondi Beach';
      const heading = this.getAttribute('heading') || product.title;
      const body = this.getAttribute('body') || '';
      const cta = this.getAttribute('cta-label') || 'View product';
      const unavailable = !available.length;
      const priceText = minPrice ? `${pricePrefix}${money.format(minPrice / 100)}` : '';

      this.innerHTML = `
        <article class="apc-card">
          <a class="apc-card__media" href="${this.productUrl}" aria-label="View ${escapeHtml(product.title)}">
            ${image ? `<img src="${escapeHtml(cleanImageUrl(image, 900))}" alt="${escapeHtml(product.title)}" loading="lazy" width="900" height="675">` : ''}
          </a>
          <div class="apc-card__content">
            <p class="apc-card__eyebrow">${escapeHtml(eyebrow)}</p>
            <div class="apc-card__heading-row">
              <h3 class="apc-card__title">${escapeHtml(heading)}</h3>
              ${priceText ? `<p class="apc-card__price">${escapeHtml(priceText)}</p>` : ''}
            </div>
            ${body ? `<p class="apc-card__body">${escapeHtml(body)}</p>` : ''}
            <a class="apc-card__cta${unavailable ? ' apc-card__cta--muted' : ''}" href="${this.productUrl}">
              ${escapeHtml(unavailable ? 'View availability' : cta)}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </article>`;
      this.removeAttribute('aria-busy');
    }

    renderError() {
      const heading = this.getAttribute('heading') || 'Explore Argentum';
      const body = this.getAttribute('body') || 'See current options and availability.';
      const cta = this.getAttribute('cta-label') || 'View product';
      this.innerHTML = `
        <article class="apc-card apc-card--fallback">
          <div class="apc-card__content">
            <p class="apc-card__eyebrow">Born in Bondi Beach</p>
            <h3 class="apc-card__title">${escapeHtml(heading)}</h3>
            <p class="apc-card__body">${escapeHtml(body)}</p>
            <a class="apc-card__cta" href="${this.productUrl}">${escapeHtml(cta)} <span aria-hidden="true">→</span></a>
          </div>
        </article>`;
      this.removeAttribute('aria-busy');
    }
  }

  if (!customElements.get('argentum-product-card')) {
    customElements.define('argentum-product-card', ArgentumProductCard);
  }
})();
